import React from 'react';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import ItemSentInfo from '../ItemSentInfo';
import ItemStatusChips from '../ItemStatusChips';
import { areaLabel, typeInfo } from '../../../../../components/changelog/changelogConfig';
import { formatDate } from '../../changelogAdminConfig';

const HEADERS = ['Imagem', 'Novidade', 'Tipo', 'Área', 'Status', 'Envios', 'Data', ''];
const cellSx = { fontSize: '0.8125rem', verticalAlign: 'top' };
const clampSx = { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' };

const thumbSx = {
  width: 72, height: 45, borderRadius: '6px', border: '1px solid #E2E8F0', flexShrink: 0,
  display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', bgcolor: '#F8F9FC',
  cursor: 'pointer',
};

// Miniatura da imagem da novidade; sem imagem, um quadro vazio. Passar o mouse amplia.
function ItemThumb({ image, onClick }) {
  if (!image?.url) {
    return (
      <Tooltip title="Sem imagem">
        <Box sx={{ ...thumbSx, borderStyle: 'dashed' }} onClick={onClick}>
          <ImageOutlinedIcon sx={{ fontSize: 20, color: '#CBD5E1' }} />
        </Box>
      </Tooltip>
    );
  }
  return (
    <Tooltip
      placement="right"
      componentsProps={{ tooltip: { sx: { bgcolor: '#fff', p: 0.5, boxShadow: 3, maxWidth: 'none' } } }}
      title={<Box component="img" src={image.url} alt="" sx={{ display: 'block', width: 320, borderRadius: '6px' }} />}
    >
      <Box sx={thumbSx} onClick={onClick}>
        <Box component="img" src={image.url} alt="" loading="lazy" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      </Box>
    </Tooltip>
  );
}

function CatalogRow({ item, selected, onToggle, onEdit, onPreview }) {
  const type = typeInfo(item.type);
  return (
    <TableRow hover selected={selected}>
      <TableCell padding="checkbox" sx={{ verticalAlign: 'top' }}>
        <Checkbox size="small" checked={selected} onChange={() => onToggle(item)} inputProps={{ 'aria-label': `Selecionar ${item.title}` }} />
      </TableCell>
      <TableCell sx={{ ...cellSx, width: 88 }}><ItemThumb image={item.image} onClick={() => onEdit(item)} /></TableCell>
      <TableCell sx={{ ...cellSx, minWidth: 260, maxWidth: 420 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {Boolean(Number(item.highlight)) && (
            <Tooltip title="Destaque"><StarRoundedIcon sx={{ fontSize: 16, color: '#F59E0B' }} /></Tooltip>
          )}
          {item.title || 'Sem título'}
        </Typography>
        {item.summary && <Typography variant="caption" color="text.secondary" sx={clampSx}>{item.summary}</Typography>}
        <Typography variant="caption" color="text.disabled" component="div">{item.slug}</Typography>
      </TableCell>
      <TableCell sx={{ ...cellSx, whiteSpace: 'nowrap', color: type.color, fontWeight: 600 }}>{type.label}</TableCell>
      <TableCell sx={{ ...cellSx, whiteSpace: 'nowrap' }}>{areaLabel(item.area) || '—'}</TableCell>
      <TableCell sx={cellSx}><ItemStatusChips item={item} /></TableCell>
      <TableCell sx={{ ...cellSx, minWidth: 170 }}><ItemSentInfo item={item} /></TableCell>
      <TableCell sx={{ ...cellSx, whiteSpace: 'nowrap' }}>{formatDate(item.item_date)}</TableCell>
      <TableCell sx={{ ...cellSx, width: 88, whiteSpace: 'nowrap' }} align="right">
        <Tooltip title="Prévia">
          <IconButton size="small" onClick={() => onPreview(item)}><VisibilityOutlinedIcon fontSize="small" /></IconButton>
        </Tooltip>
        <Tooltip title="Editar">
          <IconButton size="small" onClick={() => onEdit(item)}><EditOutlinedIcon fontSize="small" /></IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}

/**
 * Tabela do catálogo de novidades, com seleção para as ações em lote.
 * @param {{ items: Array, isLoading: boolean, hasActiveFilters: boolean, selectedIds: Set,
 *   onToggle: Function, onTogglePage: Function, onEdit: Function, onPreview: Function }} props
 */
function CatalogTable({ items, isLoading, hasActiveFilters, selectedIds, onToggle, onTogglePage, onEdit, onPreview }) {
  const selectedOnPage = items.filter((item) => selectedIds.has(item.id)).length;
  const allOnPage = items.length > 0 && selectedOnPage === items.length;

  return (
    <TableContainer sx={{ overflowX: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: '#F8F9FC' }}>
            <TableCell padding="checkbox">
              <Checkbox
                size="small"
                checked={allOnPage}
                indeterminate={selectedOnPage > 0 && !allOnPage}
                onChange={() => onTogglePage(items, !allOnPage)}
                inputProps={{ 'aria-label': 'Selecionar a página' }}
              />
            </TableCell>
            {HEADERS.map((label, index) => (
              <TableCell key={label || index} sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.8125rem', whiteSpace: 'nowrap' }}>
                {label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={HEADERS.length + 1} align="center" sx={{ py: 6 }}><CircularProgress size={32} /></TableCell>
            </TableRow>
          ) : items.length === 0 ? (
            <TableRow>
              <TableCell colSpan={HEADERS.length + 1} align="center" sx={{ py: 6 }}>
                <Typography variant="body2" color="text.secondary">
                  {hasActiveFilters ? 'Nenhuma novidade com esses filtros' : 'Nenhuma novidade no catálogo ainda'}
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            items.map((item) => (
              <CatalogRow key={item.id} item={item} selected={selectedIds.has(item.id)} onToggle={onToggle} onEdit={onEdit} onPreview={onPreview} />
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default CatalogTable;
